// Misc
import React, { Fragment } from 'react'
import Loadable from 'react-loadable'
import PropTypes from 'prop-types'
import { Redirect, Route } from 'react-router-dom'
import Switch from 'react-router-transition-group'
import Fade from '@material-ui/core/Fade'
import ContentLoader from 'atoms/ContentLoader'
import { AppProgressActivator } from 'atoms/AppProgress'
import UserLayout from 'components/Layout/UserLayout'
import { RedirectWithQuery } from 'utils/router'
import withWidth, { isWidthDown } from '@material-ui/core/withWidth'

const LoadablePage = loader => Loadable({
  loader,
  loading: () => (
    <Fragment>
      <ContentLoader />
      <AppProgressActivator />
    </Fragment>
  )
})

// Pages
const BuildPage = LoadablePage(() => import('./Build'))
const BusinessPage = LoadablePage(() => import('./Business'))
const MePage = LoadablePage(() => import('./Me'))
const MeManagerPage = LoadablePage(() => import('./MeManager'))
const SharePage = LoadablePage(() => import('./Share'))
const CustomizePage = LoadablePage(() => import('./Customize'))
const InboxPage = LoadablePage(() => import('./Inbox'))
const InboxChatPage = LoadablePage(() => import('./InboxChat'))
const ManagersPage = LoadablePage(() => import('./Managers'))
const ManagerCreatePage = LoadablePage(() => import('./ManagerCreate'))
const ManagerEditPage = LoadablePage(() => import('./ManagerEdit'))
const BillingPage = LoadablePage(() => import('./Billing'))

// Integration pages
const IntegrationsPage = LoadablePage(() => import('./Integrations'))
const IntegrationStripePage = LoadablePage(() => import('./IntegrationStripe'))
const IntegrationCalendarPage = LoadablePage(() => import('./IntegrationCalendar'))
const IntegrationZapierPage = LoadablePage(() => import('./IntegrationZapier'))
const IntegrationZapierZapsPage = LoadablePage(() => import('./IntegrationZapierZaps'))
const IntegrationWorkflowMaxPage = LoadablePage(() => import('./IntegrationWorkflowMax'))
const IntegrationMailChimpPage = LoadablePage(() => import('./IntegrationMailChimp'))
const IntegrationSlackPage = LoadablePage(() => import('./IntegrationSlack'))
const IntegrationMindbodyPage = LoadablePage(() => import('./IntegrationMindbody'))
const StatsPage = LoadablePage(() => import('./Stats'))

const LoggedUserRoutes = ({ width }) => (
  <UserLayout>
    <Switch transition={Fade}>
      <Redirect exact from="/login" to="/inbox" />
      <Redirect exact from="/" to="/inbox" />
      <Redirect exact from="/livechat/chat/:liveChatUuid" to="/inbox/livechat/:liveChatUuid" />
      <Redirect exact from="/profile" to="/business" />

      <RedirectWithQuery exact from="/profile/stripe" to="/integrations/stripe" />
      <RedirectWithQuery exact from="/profile/calendar/config" to="/integrations/calendar" />
      <RedirectWithQuery exact from="/profile/zapier/config" to="/integrations/zapier" />
      <RedirectWithQuery exact from="/profile/mailchimp/config" to="/integrations/mailchimp" />

      <Route exact path="/build/:proposalUuid?/:action?" component={BuildPage} />
      <Route exact path="/business" component={BusinessPage} />
      <Route exact path="/me" component={MePage} />
      <Route exact path="/me/manager" component={MeManagerPage} />
      <Route exact path="/share" component={SharePage} />
      <Route exact path="/customize" component={CustomizePage} />
      <Route exact path="/billing" component={BillingPage} />
      <Route exact path="/stats" component={StatsPage} />

      <Route exact path="/integrations" component={IntegrationsPage} />
      <Route exact path="/integrations/stripe" component={IntegrationStripePage} />
      <Route exact path="/integrations/calendar" component={IntegrationCalendarPage} />
      <Route exact path="/integrations/zapier" component={IntegrationZapierPage} />
      <Route exact path="/integrations/zapier/zaps" component={IntegrationZapierZapsPage} />
      <Route exact path="/integrations/workflowmax" component={IntegrationWorkflowMaxPage} />
      <Route exact path="/integrations/mindbody" component={IntegrationMindbodyPage} />
      <Route exact path="/integrations/mailchimp" component={IntegrationMailChimpPage} />
      <Route exact path="/integrations/slack" component={IntegrationSlackPage} />

      <Route exact path="/team" component={ManagersPage} />
      <Route exact path="/team/create" component={ManagerCreatePage} />
      <Route exact path="/team/:managerUuid" component={ManagerEditPage} />

      <Route transitionKey="inbox" exact path="/inbox" component={InboxPage} />
      <Route transitionKey="inbox" exact path="/inbox/status/:status" component={InboxPage} />
      <Route transitionKey="inbox" exact path="/inbox/proposal/:proposalId" component={InboxPage} />
      <Route transitionKey="inbox" exact path="/inbox/proposal/:proposalId/status/:status" component={InboxPage} />

      <Route exact path="/inbox/request/:requestId/:action?" component={isWidthDown('sm', width) ? InboxChatPage : InboxPage} />
      <Route exact path="/inbox/livechat/:liveChatUuid" component={isWidthDown('sm', width) ? InboxChatPage : InboxPage} />

      <Route transitionKey="inbox" component={InboxPage} />
    </Switch>
  </UserLayout>
)

LoggedUserRoutes.propTypes = {
  width: PropTypes.string.isRequired
}

export default withWidth()(LoggedUserRoutes)
